import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ArrowLeft, Plus, Trash2, Edit, UserPlus, Shield, Users as UsersIcon, Key } from 'lucide-react';
import toast from 'react-hot-toast';
import { Badge } from '@/components/ui/badge';
import PermissionsDialog from '@/components/PermissionsDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminUsers() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [stores, setStores] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [showPermissionsDialog, setShowPermissionsDialog] = useState(false);
  const [selectedUserForPermissions, setSelectedUserForPermissions] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'colaborador',
    supervisor_id: '',
    tienda_id: ''
  });

  const handleOpenPermissions = (user) => {
    setSelectedUserForPermissions(user);
    setShowPermissionsDialog(true);
  };

  const handleCloseDialog = () => {
    setShowDialog(false);
    setEditingUser(null);
    // Reset form after dialog closes
    setTimeout(() => {
      setFormData({ name: '', email: '', password: '', role: 'colaborador', supervisor_id: '', tienda_id: '' });
    }, 300);
  };

  useEffect(() => {
    fetchUsers();
    fetchStores();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/users`);
      setUsers(response.data);
    } catch (error) {
      toast.error('Error al cargar usuarios');
    }
  };

  const fetchStores = async () => {
    try {
      const response = await axios.get(`${API}/stores`);
      setStores(response.data);
    } catch (error) {
      // no-op
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (editingUser) {
        // Update existing user
        await axios.put(`${API}/admin/users/${editingUser.id}`, formData);
        toast.success('Usuario actualizado exitosamente');
      } else {
        // Create new user
        await axios.post(`${API}/admin/users`, formData);
        toast.success('Usuario creado exitosamente');
      }
      handleCloseDialog();
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Error al ${editingUser ? 'actualizar' : 'crear'} usuario`);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({
      name: user.name,
      email: user.email,
      password: '', // Don't pre-fill password
      role: user.role,
      supervisor_id: user.supervisor_id || '',
      tienda_id: user.tienda_id || ''
    });
    setShowDialog(true);
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('¿Estás seguro de eliminar este usuario?')) return;

    try {
      await axios.delete(`${API}/admin/users/${userId}`);
      toast.success('Usuario eliminado');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar usuario');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/40">
      <div className="container mx-auto px-4 py-8">
        <div className="mb-6 flex items-center justify-between">
          <Button variant="ghost" onClick={() => navigate('/dashboard')}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Volver
          </Button>
          <Button onClick={() => setShowDialog(true)} className="bg-[#2c57c5] hover:bg-[#1e3a8a] text-white">
            <Plus className="mr-2 h-4 w-4" />
            Nuevo Usuario
          </Button>
        </div>

        <Card className="p-6">
          <h1 className="text-3xl font-bold text-slate-900 mb-6">Gestión de Usuarios</h1>

          <div className="space-y-4">
            {users.map(user => {
              const getRoleBadge = (role) => {
                const badges = {
                  admin: { label: 'Administrador', color: 'bg-purple-100 text-purple-700 border-purple-300' },
                  gerencia: { label: 'Gerencia', color: 'bg-blue-100 text-blue-700 border-blue-300' },
                  supervisor: { label: 'Supervisor', color: 'bg-green-100 text-green-700 border-green-300' },
                  encargada_compras: { label: 'Enc. Compras', color: 'bg-orange-100 text-orange-700 border-orange-300' },
                  asistente_admin: { label: 'Asistente Admin', color: 'bg-cyan-100 text-cyan-700 border-cyan-300' },
                  colaborador: { label: 'Colaborador', color: 'bg-gray-100 text-gray-700 border-gray-300' }
                };
                return badges[role] || badges.colaborador;
              };
              
              const roleBadge = getRoleBadge(user.role);
              const supervisor = user.supervisor_id ? users.find(u => u.id === user.supervisor_id) : null;
              const userStore = user.tienda_id ? stores.find(s => s.id === user.tienda_id) : null;
              
              return (
                <Card key={user.id} className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-1">
                        <h3 className="font-semibold text-slate-900">{user.name}</h3>
                        <Badge className={`${roleBadge.color} border`}>
                          {user.role === 'admin' && <Shield className="h-3 w-3 mr-1" />}
                          {roleBadge.label}
                        </Badge>
                      </div>
                      <p className="text-sm text-slate-600">{user.email}</p>
                      {supervisor && (
                        <p className="text-xs text-slate-500 mt-1 flex items-center gap-1">
                          <UsersIcon className="h-3 w-3" />
                          Supervisor: {supervisor.name}
                        </p>
                      )}
                      {userStore && (
                        <p className="text-xs text-slate-500 mt-1 flex items-center gap-1">
                          🏪 Tienda: {userStore.nombre}
                        </p>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleOpenPermissions(user)}
                        className="text-[#2c57c5] hover:text-[#1e3a8a]"
                        title="Gestionar Permisos"
                      >
                        <Key className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEdit(user)}
                        className="text-blue-600 hover:text-blue-700"
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      {user.role !== 'admin' && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDelete(user.id)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        </Card>
      </div>

      {/* Create/Edit User Dialog */}
      {showDialog && (
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingUser ? 'Editar Usuario' : 'Crear Nuevo Usuario'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Nombre Completo *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Email *</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Contraseña {editingUser ? '(Dejar vacío para no cambiar)' : '*'}</Label>
              <Input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required={!editingUser}
                minLength={6}
                placeholder={editingUser ? 'Dejar vacío si no quieres cambiarla' : ''}
              />
            </div>
            <div className="space-y-2">
              <Label>Rol *</Label>
              <select 
                value={formData.role} 
                onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              >
                <option value="colaborador">Colaborador</option>
                <option value="supervisor">Supervisor</option>
                <option value="encargada_compras">Encargada de Compras</option>
                <option value="asistente_admin">Asistente Administrativo</option>
                <option value="gerencia">Gerencia</option>
                <option value="admin">Administrador (Solo puede haber 1)</option>
              </select>
            </div>
            
            {formData.role === 'colaborador' && (
              <div className="space-y-2">
                <Label>Tienda asignada (Opcional)</Label>
                <select
                  value={formData.tienda_id}
                  onChange={(e) => setFormData({ ...formData, tienda_id: e.target.value })}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  data-testid="user-tienda-select"
                >
                  <option value="">Sin restriccion (puede pagar de cualquier tienda)</option>
                  {stores.map(store => (
                    <option key={store.id} value={store.id}>
                      {store.nombre} {store.codigo && `(${store.codigo})`}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-slate-500">
                  Si se asigna una tienda, este colaborador solo podra crear pagos para esa tienda.
                </p>
              </div>
            )}

            {formData.role === 'colaborador' && (
              <div className="space-y-2">
                <Label>Supervisor (Opcional)</Label>
                <select 
                  value={formData.supervisor_id} 
                  onChange={(e) => setFormData({ ...formData, supervisor_id: e.target.value })}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                >
                  <option value="">Sin supervisor</option>
                  {users.filter(u => u.role === 'supervisor').map(supervisor => (
                    <option key={supervisor.id} value={supervisor.id}>
                      {supervisor.name} ({supervisor.email})
                    </option>
                  ))}
                </select>
                <p className="text-xs text-slate-500">
                  El supervisor podrá ver los pagos de este colaborador
                </p>
              </div>
            )}
            <div className="flex justify-end space-x-2">
              <Button type="button" variant="outline" onClick={handleCloseDialog}>
                Cancelar
              </Button>
              <Button type="submit" disabled={loading} className="bg-[#2c57c5] hover:bg-[#1e3a8a] text-white">
                {editingUser ? <Edit className="mr-2 h-4 w-4" /> : <UserPlus className="mr-2 h-4 w-4" />}
                {loading ? (editingUser ? 'Actualizando...' : 'Creando...') : (editingUser ? 'Actualizar Usuario' : 'Crear Usuario')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
      )}

      {/* Permissions Dialog */}
      <PermissionsDialog
        user={selectedUserForPermissions}
        open={showPermissionsDialog}
        onClose={() => {
          setShowPermissionsDialog(false);
          setSelectedUserForPermissions(null);
        }}
        onSave={fetchUsers}
      />
    </div>
  );
}
